import React, { useState, useContext, useCallback } from "react";
import { Context } from "../../../../index";
import { observer } from "mobx-react-lite";
import UniversalForm from "../../../../components/forms/UniversalForm";
import ButtonComponent from "../../../../components/buttons/ButtonComponent";
import SquareButton from "../../../../components/buttons/SquareButton";
import SVG from "../../../../components/svg/Svg";
import Modal from "../../../../components/forms/wrap/Modal";
import { useModal } from "../../../../components/hooks/useModal";

import "./styles.css";
import { logApiError } from "../../../../utils/logApiError";

export default observer(function Lines() {
  const { uiStore, inplantsStore } = useContext(Context);
  const modal = useModal(false); // ✅ Универсальный контроллер модалки

  const sortedLines = React.useMemo(() => {
    return [...inplantsStore.lines].sort((a, b) => a.id - b.id);
  }, [inplantsStore.lines]);

  const visibleLines = sortedLines;

  const [mode, setMode] = useState<"create" | "edit">("create");
  const [isHide, setHide] = useState(true);

  // --- Открытие форм ---
  const openCreate = useCallback(() => {
    if (!inplantsStore.plant) {
      alert("Не выбран объект");
      return;
    }
    setMode("create");
    inplantsStore.setActiveLine(null);
    modal.open();
  }, [inplantsStore.plant, modal]); // eslint-disable-line react-hooks/exhaustive-deps

  const openSettings = useCallback(
    (line) => {
      inplantsStore.setActiveLine(line);
      setMode("edit");
      modal.open();
    },
    [inplantsStore, modal],
  );

  // --- Закрытие формы ---
  const handleCloseModal = useCallback(() => {
    inplantsStore.setActiveLine(null);
    setMode("create");
    modal.close();
  }, [modal]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Работа с данными ---
  const createNewLine = useCallback(
    async (data) => {
      await inplantsStore.createLine(data);
      handleCloseModal();
    },
    [inplantsStore, handleCloseModal],
  );

  const editLine = useCallback(
    async (data) => {
      try {
        // Добавляем id из activeLine
        const lineData = { ...data, id: inplantsStore.activeLine?.id };
        console.log("📝 Редактирование линии:", lineData);
        await inplantsStore.updateLine(lineData);
      } catch (error) {
        logApiError("❌ Ошибка при обновлении линии:", error);
        setTimeout(handleCloseModal, 100);
      }
    },
    [inplantsStore, handleCloseModal],
  );

  const deleteLine = useCallback(async () => {
    if (inplantsStore.activeLine?.id) {
      await inplantsStore.deleteLine(inplantsStore.activeLine.id);
      handleCloseModal();
    }
  }, [inplantsStore, handleCloseModal]);

  const getModuleWord = (count: number) => {
    if (count % 10 === 1 && count % 100 !== 11) return "модуль";
    if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) {
      return "модуля";
    }
    return "модулей";
  };

  const validateLineDelete = useCallback(() => {
    const lineId = inplantsStore.activeLine?.id;
    if (!lineId) return undefined;

    const count = inplantsStore.modules.filter((m) => m.line_id === lineId).length;
    if (count > 0) {
      return `Нельзя удалить линию: к ней привязано ${count} ${getModuleWord(count)}`;
    }
    return undefined;
  }, [inplantsStore.activeLine, inplantsStore.modules]);

  // --- Скрытие таблицы ---
  const toggleLines = useCallback(() => {
    setHide((prev) => !prev);
  }, []);

  // --- Проверка статуса линии ---
  const getLineStatus = useCallback(
    (lineId: number) => {
      const lineModules = inplantsStore.modules.filter(
        (mod) => mod.line_id === lineId,
      );
      // Линия онлайн, если есть хоть один модуль онлайн
      // Линия оффлайн, если все модули оффлайн (или нет модулей)
      return lineModules.some((mod) => (mod as any).status === "online")
        ? "online"
        : "offline";
    },
    [inplantsStore.modules],
  );

  return (
    <>
      <div className="lines_container">
        <div className="lines_head">
          <div className="lines_head_title">ЛИНИИ ОБЪЕКТА {SVG["line"]}</div>

          <div className="lines_headbtns">
            <ButtonComponent
              title="Добавить линию"
              svg="create"
              onClick={openCreate}
              size="180px"
            />
            <SquareButton
              icon={isHide ? "close_colored" : "creat_colored"}
              background="white"
              border="2px solid #1894c3"
              size="36px"
              onClick={toggleLines}
            />
          </div>
        </div>

        {isHide && (
          <div className="lines_table_container">
            {visibleLines?.map((el, index) => {
              const lineStatus = getLineStatus(el.id);
              return (
                <div
                  className={`${lineStatus === "online" ? "cur_line" : "cur_line_off"} ${
                    uiStore.selectedLineId === el.id ? "cur_line--selected" : ""
                  }`}
                  key={index}
                  onClick={() => uiStore.setSelectedLineId(el.id)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="cur_line_name">
                    <div className="cur_lines_title">{el.name}</div>
                  </div>
                  <div
                    className={
                      lineStatus === "online"
                        ? "cur_line_params"
                        : "cur_line_params_off"
                    }
                  >
                    <div>Текущий ток, Фаза A - {el.limit_A || 0} A</div>
                    <div>Текущий ток, Фаза B - {el.limit_B || 0} A</div>
                    <div>Текущий ток, Фаза C - {el.limit_C || 0} A</div>
                    <div>Лимит тока линии: {el.current_limit_line || 0} A</div>
                  </div>
                  <div className="cur_line_controls">
                    <div
                      className="cur_line_action"
                      onClick={() => openSettings(el)}
                    >
                      {SVG["pen_white"]}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ✅ Единственная модалка с одной формой */}
      <Modal isOpen={modal.isOpen} onClose={handleCloseModal}>
        <UniversalForm
          mode={mode}
          handleCloseModal={handleCloseModal}
          entityData={inplantsStore.activeLine}
          fields={{
            name: { label: "Название", required: true },
            current_limit_line: { label: "Лимит тока линии", required: true },
          }}
          createEntity={createNewLine}
          editEntity={editLine}
          deleteEntity={deleteLine}
          validateDelete={validateLineDelete}
          title="Создать новую линию"
          entity="линию"
        />
      </Modal>
    </>
  );
});
